import { Grid } from "@mui/material";
import { useFormik } from "formik";
import { useCallback } from "react";
import DropZone from "../../../../../components/app/DropZone";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import CustomTextArea from "../../../../../components/CustomTextArea";
import StyledFormModal from "../../../../../components/globalModal/StyledModal";
import Input from "../../../../../components/Input";
import NumberInput from "../../../../../components/NumberInput";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAddFaxSend } from "../../../../../queries/communication/fax-integration/use-add-fax-send";
import {
  convertToBase64,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";
import {
  DOCUMENT_INITIAL_VALUE,
  DocumentForm,
  FAX_ALLOWED_FILE_PATTERN,
  FAX_DOCUMENT_VALIDATION_SCHEMA,
  FAX_FILE_READ_ERROR_MESSAGE,
  FAX_SUPPORTED_FILE_LABEL
} from "./fax-document-constant";

interface UploadFaxDocumentDrawerProps {
  disclosureHost: DisclosureHost;
  allowSecondDocument?: boolean;
}

const UploadFaxDocumentDrawer = ({
  disclosureHost,
  allowSecondDocument = false,
}: UploadFaxDocumentDrawerProps) => {
  const showSnackbar = useSnackbar();

  const formik = useFormik<DocumentForm>({
    initialValues: disclosureHost.data || DOCUMENT_INITIAL_VALUE,
    validationSchema: FAX_DOCUMENT_VALIDATION_SCHEMA,
    onSubmit: submitDocumentHandler,
    enableReinitialize: true,
  });

  const { mutateAsync: addFaxSend } = useAddFaxSend();

  /* The name is recorded before any read so the schema always sees the chosen
     file, and an unsupported one is never handed to convertToBase64, which
     rejects on large or unreadable input. */
  const selectFaxFile = useCallback(
    async (
      files: FileList | null,
      nameField: "name" | "sFileName_2",
      contentField: "file" | "sFileContent_2"
    ) => {
      const curFile = files?.item(0);

      formik.setFieldTouched(nameField, true, false);
      formik.setFieldValue(nameField, curFile?.name ?? "");

      if (!curFile || !FAX_ALLOWED_FILE_PATTERN.test(curFile.name)) {
        formik.setFieldValue(contentField, null);
        return;
      }

      try {
        formik.setFieldValue(contentField, await convertToBase64(curFile));
      } catch {
        formik.setFieldValue(nameField, "");
        formik.setFieldValue(contentField, null);
        showSnackbar({ type: "error", message: FAX_FILE_READ_ERROR_MESSAGE });
      }
    },
    [formik, showSnackbar]
  );

  async function submitDocumentHandler(values?: DocumentForm) {
    const payload: any = {
      sFaxType: values?.documentType ? values?.documentType : "SINGLE",
      sToFaxNumber: values?.toFaxNumber,
      sFaxFromHeader: values?.sCPFromName || undefined,
      sCPFromName: values?.sCPFromName || undefined,
      sFileName_1: values?.name,
      sFileContent_1: values?.file,
      sFileName_2: allowSecondDocument ? values?.sFileName_2 : undefined,
      sFileContent_2: allowSecondDocument ? values?.sFileContent_2 : undefined,
      documentDescription: values?.documentDescription,
    };

    showToastAndResetForm(
      addFaxSend({ faxSend: payload, xTENANTID: getXtenantId() })
    );
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    await apiCall
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        closeAndReset();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function closeAndReset() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndReset}
      dialogTitle="Outgoing Fax"
      dialogAction={
        <StyledButtonV2
          label="Send"
          variant="contained"
          onClick={formik.submitForm}
          sx={{ width: "130px" }}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: allowSecondDocument ? "900px" : "600px",
        },
      }}
    >
      <Grid container spacing={3} pt={2}>
        <Grid item xs={6}>
          <Input
            label="Sender Name"
            textFieldProps={{
              placeholder: "Enter Sender Name",
              name: "sCPFromName",
              value: formik.values.sCPFromName,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            }}
            error={formik.touched.sCPFromName && formik.errors.sCPFromName}
          />
        </Grid>
        {/* To Fax Number */}
        <Grid item xs={6}>
          <NumberInput
            required
            label="To Fax Number"
            textFieldProps={{
              placeholder: "Enter Fax Number",
              name: "toFaxNumber",
              value: formik.values.toFaxNumber,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            }}
            error={
              formik.touched.toFaxNumber && (formik.errors.toFaxNumber as any)
            }
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Document Description"
            id="outlined-multiline-static"
            rows={2}
            variant="outlined"
            name="documentDescription"
            placeholder="Type Description here"
            onBlur={formik.handleBlur}
            value={formik.values?.documentDescription}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={allowSecondDocument ? 6 : 12} mt={2}>
          <DropZone
            supportedFile={FAX_SUPPORTED_FILE_LABEL}
            value={formik.values?.name as any}
            height={"243px"}
            accept="all"
            content="Drag & Drop files Or Browse Files"
            required
            onClick={(file) => selectFaxFile(file, "name", "file")}
            error={formik.touched.name && formik.errors.name}
          />
        </Grid>
        {allowSecondDocument && (
          <Grid item xs={6} mt={2}>
            <DropZone
              supportedFile={FAX_SUPPORTED_FILE_LABEL}
              value={formik.values?.sFileName_2 as any}
              height={"243px"}
              accept="all"
              content="Drag & Drop files Or Browse Files"
              onClick={(file) =>
                selectFaxFile(file, "sFileName_2", "sFileContent_2")
              }
              error={formik.touched.sFileName_2 && formik.errors.sFileName_2}
            />
          </Grid>
        )}
      </Grid>
    </StyledFormModal>
  );
};

export default UploadFaxDocumentDrawer;

// "sFileName_1"
//   "sFileContent_1"
//   "sFileName_2"
//   "sFileContent_2"
